import React, { useMemo } from 'react';
import { formatDisplayTime, getZonedDateTimeParts, isSameZonedCalendarDay } from '../../common/ecxTimeUtils';
import AppointmentListItem from './AppointmentListItem';
import { parseAppointmentTimeToMinutes } from './subparts/appointmentsQuery';

const COLUMN_COUNT = 8;

const formatNowLabel = (hour, minute) =>
    formatDisplayTime(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);

const getNowMarkerIndex = (appointments, nowMinutes) => {
    const index = appointments.findIndex((appt) => {
        const start = parseAppointmentTimeToMinutes(appt.startTime);
        let end = parseAppointmentTimeToMinutes(appt.endTime);

        if (start >= Number.MAX_SAFE_INTEGER) {
            return false;
        }

        if (end >= Number.MAX_SAFE_INTEGER || end <= start) {
            end = start + 15;
        }

        // Keep the marker on the in-progress appointment (not the next upcoming one).
        return end > nowMinutes;
    });

    return index === -1 ? appointments.length : index;
};

function AppointmentListNowMarker({ label }) {
    return (
        <tr className="appointments-list-now" aria-label={`Current time ${label}`}>
            <td colSpan={COLUMN_COUNT}>
                <div className="appointments-list-now__content">
                    <span className="appointments-list-now__dot" aria-hidden="true" />
                    <span className="appointments-list-now__label">{label}</span>
                    <span className="appointments-list-now__line" aria-hidden="true" />
                </div>
            </td>
        </tr>
    );
}

const AppointmentList = ({
    appointments,
    selectedDate,
    clinicZoneId = '',
    nowTick = 0,
    checkedItems,
    onItemCheckChange,
    allSelected,
    onToggleAll,
    onStatusUpdated,
}) => {
    const nowMarker = useMemo(() => {
        const viewing =
            selectedDate instanceof Date
                ? selectedDate
                : selectedDate
                  ? new Date(selectedDate)
                  : null;

        if (!viewing || !isSameZonedCalendarDay(viewing, clinicZoneId)) {
            return null;
        }

        const zonedNow = getZonedDateTimeParts(clinicZoneId);

        return {
            index: getNowMarkerIndex(appointments, zonedNow.totalMinutes),
            label: formatNowLabel(zonedNow.hour, zonedNow.minute),
        };
    }, [appointments, clinicZoneId, nowTick, selectedDate]);

    return (
        <div className="appointments-table-wrap table-responsive">
            <table className="table table-hover align-middle mb-0 appointments-table">
                <thead>
                    <tr>
                        <th className="appointments-row__checkbox">
                            <label className="appointments-select-all appointments-select-all--table">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={allSelected}
                                    ref={(input) => {
                                        if (input) {
                                            input.indeterminate =
                                                checkedItems.length > 0 && !allSelected;
                                        }
                                    }}
                                    onChange={onToggleAll}
                                    aria-label="Select all appointments"
                                />
                            </label>
                        </th>
                        <th>Patient</th>
                        <th>Time</th>
                        <th>Doctor</th>
                        <th>Contact</th>
                        <th>Reminders</th>
                        <th>Status</th>
                        <th className="text-end">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {appointments.map((appt, index) => (
                        <React.Fragment key={appt.id}>
                            {nowMarker && nowMarker.index === index && (
                                <AppointmentListNowMarker label={nowMarker.label} />
                            )}
                            <AppointmentListItem
                                appt={appt}
                                isSelected={checkedItems.includes(appt.id)}
                                onCheckChange={onItemCheckChange}
                                onStatusUpdated={onStatusUpdated}
                            />
                        </React.Fragment>
                    ))}
                    {nowMarker && nowMarker.index === appointments.length && (
                        <AppointmentListNowMarker label={nowMarker.label} />
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default AppointmentList;
